import React, {useState, useRef, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../security/AuthContext';
import {apiClient} from '../api/ApiClient';
import {formatSignupStep2Request} from './SignupStep2Request';
import './SignupStep2Component.css';

const SignupStep2Component = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  const canvasRef = useRef(null);

  // State for form fields
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    education: '',
    ethnicity: '',
    headline: ''
  });

  // State for form validation
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // State for image handling
  const [image, setImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({x: 0, y: 0});
  const [isDragging, setIsDragging] = useState(false);
  const [lastPosition, setLastPosition] = useState({x: 0, y: 0});
  const [showSubmittedPrompt, setShowSubmittedPrompt] = useState(false);

  // Dropdown options
  const educationOptions = [
    {value: '', label: '[Not selected]'},
    {value: 'high_school', label: 'High School'},
    {value: 'some_college', label: 'Some College'},
    {value: 'associates', label: 'Associates Degree'},
    {value: 'bachelors', label: 'Bachelor\'s Degree'},
    {value: 'graduate', label: 'Graduate Degree'},
    {value: 'doctorate', label: 'Doctorate'}
  ];

  const ethnicityOptions = [
    {value: '', label: '[Not selected]'},
    {value: 'asian', label: 'Asian'},
    {value: 'black', label: 'Black / African Descent'},
    {value: 'latino', label: 'Hispanic / Latino'},
    {value: 'white', label: 'White / Caucasian'},
    {value: 'middle_eastern', label: 'Middle Eastern'},
    {value: 'native', label: 'Native American'},
    {value: 'pacific', label: 'Pacific Islander'},
    {value: 'mixed', label: 'Mixed / Multiple'},
    {value: 'other', label: 'Other'}
  ];


  // Input change handler
  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Validation
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'firstname':
      case 'lastname':
        if (!value) error = 'This field is required.';
        break;
      case 'education':
      case 'ethnicity':
        if (!value) error = 'Please make a selection.';
        break;
      case 'headline':
        if (!value) error = 'This field is required.';
        else if (value.length < 5) error = 'Headline should be at least 5 characters.';
        break;
      default:
        break;
    }

    return error;
  };

  // Handle field blur - validate when user leaves a field
  const handleBlur = (e) => {
    const {name, value} = e.target;
    const error = validateField(name, value);

    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  // Image selection handler
  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImage(e.target.result);
        setPosition({x: 0, y: 0});
        setScale(1);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  // Mouse handlers for drag functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newX = e.clientX - lastPosition.x;
    const newY = e.clientY - lastPosition.y;

    // Limit the dragging area
    const maxOffset = 200;
    const clampedX = Math.max(-maxOffset, Math.min(maxOffset, newX));
    const clampedY = Math.max(-maxOffset, Math.min(maxOffset, newY));

    setPosition({
      x: clampedX,
      y: clampedY
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();


    // Add this debugging
    const requestData = formatSignupStep2Request(formData);
    console.log('Form data before formatting:', formData);
    console.log('Formatted request data:', JSON.stringify(requestData, null, 2));


    console.log('Token available:', authContext.token);
    if (!authContext.token) {
      setErrors({
        general: 'Authentication token is missing. Please log in again.'
      });
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      return;
    }
    // Validate all fields
    const newErrors = {};
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    // Check if profile picture is uploaded
    if (!croppedImage) {
      newErrors.image = 'Profile picture is required.';
    }

    setErrors(newErrors);
    setTouched({
      firstname: true,
      lastname: true,
      education: true,
      ethnicity: true,
      headline: true,
      image: true
    });

    // If no errors, submit form
    if (Object.keys(newErrors).length === 0) {
      try {
        await uploadProfileData();
        await uploadProfilePicture();
        setShowSubmittedPrompt(true);

        // Redirect after a short delay
        setTimeout(() => {
          navigate('/email/inbox');
        }, 2000);
      } catch (error) {
        console.error('Error submitting profile:', error);
      }
    }
  };


  // For profile data upload
  const uploadProfileData = async () => {
    try {
      const requestData = formatSignupStep2Request(formData);
      console.log('Request data:', requestData);
      console.log('Using token:', authContext.token);

      const response = await apiClient.post('/rest/signup2', requestData, {
        headers: {
          'Authorization': `Bearer ${authContext.token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error uploading profile data:', error);
      // Log more detailed error info
      if (error.response) {
        console.error('Error response:', error.response.status, error.response.data);
      }
      throw error;
    }
  };


  const uploadProfilePicture = async () => {
    try {
      setUploadProgress(0);

      if (!croppedImage) {
        throw new Error('No cropped image available');
      }

      // Convert base64 to blob
      const base64Data = croppedImage.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, {type: 'image/jpeg'});
      const formData = new FormData();
      formData.append('file', blob, 'profile.jpg');

      const response = await apiClient.post('/rest/profile/photos/upload', formData, {
        headers: {
          'Authorization': `Bearer ${authContext.token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      console.log('Photo upload response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      if (error.response) {
        console.error('Error response:', error.response.status, error.response.data);
      }
      throw error;
    }
  };



  useEffect(() => {
    if (!image || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Set a dark gray background for the canvas
      ctx.fillStyle = '#333333';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Calculate the center point of the canvas
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Calculate the crop circle radius (slightly smaller than the canvas width/2)
      const radius = Math.min(canvas.width, canvas.height) / 2 - 10;

      // Calculate dimensions maintaining aspect ratio
      let drawWidth, drawHeight;
      const imgAspect = img.width / img.height;

      if (imgAspect >= 1) {
        // Landscape or square image
        drawHeight = canvas.height * scale;
        drawWidth = drawHeight * imgAspect;
      } else {
        // Portrait image
        drawWidth = canvas.width * scale;
        drawHeight = drawWidth / imgAspect;
      }

      // Calculate position to center the image while allowing for dragging
      const drawX = centerX - (drawWidth / 2) + position.x;
      const drawY = centerY - (drawHeight / 2) + position.y;

      // Draw the image on the canvas
      ctx.save();

      // Create a clipping path for the circle (for display only)
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, false);
      ctx.clip();

      // Draw the image inside the clipped circle
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

      // Restore the context to remove the clipping
      ctx.restore();

      // Draw a white circle border for the UI
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2, true);
      ctx.stroke();

      // FOR THE PREVIEW AND FINAL IMAGE: Create a square crop without circle masking
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');

      // Set dimensions for our square crop
      const cropSize = radius * 2;
      tempCanvas.width = cropSize;
      tempCanvas.height = cropSize;

      // Draw the portion of the original image directly (not from the canvas with circular mask)
      // We need to calculate what part of the original image corresponds to our crop area
      const sourceX = centerX - radius - position.x; // Adjust for panning
      const sourceY = centerY - radius - position.y; // Adjust for panning

      // Create a new image to draw the square crop without any masking
      tempCtx.drawImage(
        img,
        // Convert canvas coordinates back to original image coordinates
        (centerX - radius - drawX) / (drawWidth / img.width),
        (centerY - radius - drawY) / (drawHeight / img.height),
        (cropSize) / (drawWidth / img.width),
        (cropSize) / (drawHeight / img.height),
        // Destination is the full temp canvas
        0, 0, cropSize, cropSize
      );

      // Convert to data URL and set the cropped image
      const croppedData = tempCanvas.toDataURL('image/jpeg', 0.95);
      setCroppedImage(croppedData);
    };

    img.src = image;
  }, [image, position.x, position.y, scale]);




  // Add this useEffect for token debugging
  useEffect(() => {
    console.log('Auth context token:', authContext.token);

    if (authContext.token) {
      try {
        // Decode the JWT token to check its claims
        const tokenParts = authContext.token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('Token payload:', payload);
          console.log('MemberId in token:', payload.memberId);

          // Check if memberId is present
          if (!payload.memberId) {
            console.error('Token does not contain memberId claim');
            // Navigate to login after showing an error
            setErrors(prev => ({
              ...prev,
              general: 'Authentication is incomplete. Please log in again.'
            }));
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          }
        }
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    } else {
      console.error('No token available');
    }
  }, [authContext.token, navigate]);

  return (
    <div className="signup-wrapper">
      <div className="finshSignupProfile">
        <h3>Complete profile and start dating</h3>
      </div>

      {showSubmittedPrompt && (
        <div className="alert alert-success message-animation" role="alert">
          Thank you! Your account has been created.
        </div>
      )}

      <form onSubmit={handleSubmit} className="signup-form">
        <div className="form-group">
          <div className="signup-profile-row-48">
            <label htmlFor="firstname">First Name</label>
            <input
              id="firstname"
              name="firstname"
              className={`form-control ${touched.firstname && errors.firstname ? 'error' : ''}`}
              type="text"
              value={formData.firstname}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
            {touched.firstname && errors.firstname && (
              <div className="error-message">
                <strong>{errors.firstname}</strong>
              </div>
            )}
          </div>

          <div className="signup-profile-row-48">
            <label htmlFor="lastname">Last Name</label>
            <input
              id="lastname"
              name="lastname"
              className={`form-control ${touched.lastname && errors.lastname ? 'error' : ''}`}
              type="text"
              value={formData.lastname}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            />
            {touched.lastname && errors.lastname && (
              <div className="error-message">
                <strong>{errors.lastname}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <div className="signup-profile-row-48">
            <label htmlFor="education">Education</label>
            <select
              id="education"
              name="education"
              className={`form-control ${touched.education && errors.education ? 'error' : ''}`}
              value={formData.education}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            >
              {educationOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {touched.education && errors.education && (
              <div className="error-message">
                <strong>{errors.education}</strong>
              </div>
            )}
          </div>

          <div className="signup-profile-row-48">
            <label htmlFor="ethnicity">Ethnicity</label>
            <select
              id="ethnicity"
              name="ethnicity"
              className={`form-control ${touched.ethnicity && errors.ethnicity ? 'error' : ''}`}
              value={formData.ethnicity}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
            >
              {ethnicityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {touched.ethnicity && errors.ethnicity && (
              <div className="error-message">
                <strong>{errors.ethnicity}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="headline">Headline</label>
          <input
            id="headline"
            name="headline"
            className={`form-control ${touched.headline && errors.headline ? 'error' : ''}`}
            type="text"
            value={formData.headline}
            onChange={handleInputChange}
            onBlur={handleBlur}
            required
          />
          {touched.headline && errors.headline && (
            <div className="error-message">
              <strong>{errors.headline}</strong>
            </div>
          )}
        </div>

        <div className="form-group">
          <div className="signup-profile-row-96">
            <div>Click select, crop image and upload</div>
            <div>Drag circle around for selection - use slider to zoom</div>

            <div className="button-container">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => document.getElementById('fileInput').click()}
              >
                Select Picture
              </button>

              {croppedImage && (
                <button
                  type="submit"
                  className="btn btn-success"
                  style={{marginLeft: '10px'}}
                >
                  Save profile picture - Create My Account
                </button>
              )}
            </div>

            <input
              id="fileInput"
              type="file"
              accept="image/*"
              style={{display: 'none'}}
              onChange={handleFileSelect}
            />

            {image && (
              <div className="cropArea">
                <div
                  className="canvas-container"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={400}
                    style={{
                      backgroundColor: 'transparent',
                      borderRadius: '4px',
                      cursor: isDragging ? 'grabbing' : 'grab'
                    }}
                  />
                </div>

                <div className="zoom-container">
                  <label>Zoom</label>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={scale}
                    onChange={(e) => setScale(parseFloat(e.target.value))}
                  />
                </div>

                {croppedImage && (
                  <div className="preview-container">
                    <p>Preview:</p>
                    <img
                      src={croppedImage}
                      alt="Preview"
                      className="preview-image"
                      style={{
                        width: '300px',
                        height: '300px',
                        objectFit: 'contain',  // Changed from 'cover' to 'contain'
                        borderRadius: '0',     // Ensure no rounded corners
                        border: '1px solid #ccc' // Optional: add a border to see the image boundaries clearly
                      }}
                    />
                  </div>
                )}

                {touched.image && errors.image && (
                  <div className="error-message">
                    <strong>{errors.image}</strong>
                  </div>
                )}

                {uploadProgress > 0 && (
                  <div className="progress">
                    <div
                      className="progress-bar"
                      style={{width: `${uploadProgress}%`}}
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                )}
              </div>
                )}
             </div>             
            </div>
      </form>
    </div>
  );
};

export default SignupStep2Component;